import { useEffect, useState } from 'react'
import Layout from 'containers/Layout'
import { useTranslation } from 'react-i18next'

import apiGet from 'lib/network/apiGet'
import apiPut from 'lib/network/apiPut'
import apiPost from 'lib/network/apiPost'
import Table from 'components/pageComponents/Table'
import LoadingIcon from 'components/atoms/LoadingIcon'
import EmptyState from 'components/pageComponents/EmptyState'
import Button from 'components/atoms/Button'
import notify from 'components/atoms/Notify'
import PageFooter from 'components/paginationComponent'
import { OfficeBuildingIcon } from '@heroicons/react/outline'

const Radar = () => {
  const { t, i18n } = useTranslation()
  const isRTL = i18n.language === 'ar'

  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState([])
  const [pagination, setPagination] = useState({})

  const fetchList = async (page = 1) => {
    if (page === 1) setLoading(true)
    const response = await apiGet('companies/radar', { pageNumber: page, pageSize: 20 })
    if (response?.success) {
      const list = response?.data?.companies || []
      setItems(prev => (page === 1 ? list : [...prev, ...list]))
      setPagination(response?.data?.pagination || {})
    }
    setLoading(false)
  }

  useEffect(() => {
    fetchList(1)
  }, [])

  const eligibleCount = pagination?.totalRecords || 0

  const handleMakeInactive = company =>
    notify.deleteConfirm({
      title: t('radar.confirmTitle'),
      message: t('radar.confirmMessage', { name: company.name }),
      onConfirm: async () => {
        const response = await apiPut(`company/status/${company._id}`, { isActive: false })
        if (response?.success) {
          notify.success(t('radar.deactivated'))
          fetchList(1)
        }
      },
    })

  const handleMakeAllInactive = () =>
    notify.deleteConfirm({
      title: t('radar.confirmAllTitle'),
      message: t('radar.confirmAllMessage', { count: eligibleCount }),
      onConfirm: async () => {
        const response = await apiPost('companies/radar/deactivate', {})
        if (response?.success) {
          notify.success(t('radar.deactivated'))
          fetchList(1)
        }
      },
    })

  const columns = [
    {
      name: t('radar.company'),
      key: 'name',
      width: '70%',
      rendered: item => (
        <div className={`flex items-center ${isRTL ? 'mr-4' : 'ml-4'}`}>
          <div className="flex-shrink-0 h-10 w-10">
            {item?.logoThumbnailUrl ? (
              <img className="h-10 w-10 rounded-full object-cover" src={item.logoThumbnailUrl} alt="" />
            ) : (
              <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center shrink-0" aria-hidden>
                <OfficeBuildingIcon className="h-5 w-5 text-gray-500" strokeWidth={1.75} />
              </div>
            )}
          </div>
          <div className={isRTL ? 'mr-4' : 'ml-4'}>
            <div className="text-sm text-normal text-gray-900">{item?.name}</div>
          </div>
        </div>
      ),
    },
    {
      name: '',
      key: 'actions',
      width: '30%',
      rendered: item => (
        <div className={isRTL ? 'mr-4' : 'ml-4'}>
          <Button onClick={() => handleMakeInactive(item)}>{t('radar.makeInactive')}</Button>
        </div>
      ),
    },
  ]

  return (
    <Layout
      breadcrumbs={[{ name: t('radar.breadcrumb'), pathname: '/radar' }]}
      headerRight={
        <div className={`flex items-center justify-end ${isRTL ? 'flex-row-reverse' : ''}`}>
          <PageFooter currentPage={pagination.currentPage} totalPages={pagination.totalPages} totalRecords={pagination.totalRecords} />
          <Button type="button" className={isRTL ? 'mr-4' : 'ml-4'} disabled={eligibleCount === 0} onClick={handleMakeAllInactive}>
            {t('radar.makeAllInactive', { count: eligibleCount })}
          </Button>
        </div>
      }
      title={t('radar.title')}
    >
      <div className="flex flex-col">
        <div className="flex-1 flex items-stretch">
          <main className="flex-1">
            <div className="align-middle inline-block min-w-full">
              <div className="min-w-full">
                {!loading ? (
                  <>
                    {items?.length === 0 ? (
                      <EmptyState v2 description={t('radar.noItems')} />
                    ) : (
                      <Table
                        hasMore={pagination?.currentPage !== pagination?.totalPages}
                        fetchData={() => fetchList((pagination?.currentPage || 0) + 1)}
                        data={items}
                        columns={columns}
                      />
                    )}
                  </>
                ) : (
                  <div className="w-full h-[200px] border-0 flex justify-center items-center">
                    <LoadingIcon />
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </Layout>
  )
}

export default Radar
